import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { IconBath, IconBed, IconCarGarage, IconRulerMeasure } from '@tabler/icons-react';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { getPriceCut, isRecentlyListed } from 'components/marketplace/helpers/certaintyPanel';
import { determinePropertyActionability } from 'components/marketplace/helpers/propertyActionability';
import { useInViewAfterLoad } from 'components/shared/hooks/useInView';
import dynamic from 'next/dynamic';

import { HomeDetails } from '../HomeDetails';
import { PropertyStatus } from '../PropertySection/PropertyStatus';
import { RentalScamBanner } from '../PropertySection/RentalScamBanner';
import { SimilarListings } from '../SimilarListings';
import type { PropertyDetailsType, PropertyEngagement } from '../types';
import { PdpEngagement } from './PdpEngagement';
import { type FloorPlanPhoto, PdpFloorPlans } from './PdpFloorPlans';
import { PdpGuaranteeCard } from './PdpGuaranteeCard';
import { PdpLocationMap } from './PdpLocationMap';
import { PdpPhotoViewer, type PdpPhotoViewerHandle } from './PdpPhotoViewer';
import { PdpPriceCard } from './PdpPriceCard';
import { PdpPriceHistory } from './PdpPriceHistory';
import { PdpSchools } from './PdpSchools';
import { PdpStatusBadge } from './PdpStatusBadge';

const FooterCTA = dynamic(() => import('../FooterCTA').then((module) => module.FooterCTA), {
  ssr: false,
});

type PdpRefreshProps = {
  property: PropertyDetailsType;
  engagement?: PropertyEngagement | null;
  onShare: () => void;
  heroOverlay?: ReactNode;
};

export const PdpRefresh = ({ property, engagement, onShare, heroOverlay }: PdpRefreshProps) => {
  const priceCardRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return undefined;
    // Tall panels scroll with the page until their bottom fits in the viewport.
    const observer = new ResizeObserver(() => {
      sidebar.style.setProperty('--pdp-sidebar-height', `${sidebar.offsetHeight}px`);
    });
    observer.observe(sidebar);
    return () => observer.disconnect();
  }, []);
  const viewerRef = useRef<PdpPhotoViewerHandle>(null);
  // Floor plans zoom in the photo viewer on their own, without the photo stack.
  const openFloorPlan = useCallback((plan: FloorPlanPhoto, origin: HTMLElement) => {
    viewerRef.current?.openPhoto({ src: plan.url, alt: plan.label || 'Floor plan' }, origin);
  }, []);
  const { inView } = useInViewAfterLoad({ ref: priceCardRef, threshold: 0, initialInView: true });
  const address = formatStreetAddress(property.street, property.unitType, property.unitNumber);
  const locality = [[property.city, property.state].filter(Boolean).join(', '), property.zip]
    .filter(Boolean)
    .join(' ');
  const { isActionable } = determinePropertyActionability(property);
  const similarProperties = property.similarProperties.filter(
    (listing) => listing.listingState !== 'IN_CONTRACT',
  );
  const priceDrop =
    property.listingState === 'ON_THE_MARKET' && isActionable
      ? getPriceCut(property.listingHistory)
      : null;
  const facts = [
    {
      label: property.bedrooms === 1 ? 'Bed' : 'Beds',
      value: property.bedrooms,
      icon: <IconBed size={18} />,
    },
    {
      label: property.bathrooms === 1 ? 'Bath' : 'Baths',
      value: property.bathrooms,
      icon: <IconBath size={18} />,
    },
    {
      label: 'Sqft',
      value: property.sqFtTotalLiving?.toLocaleString('en-US'),
      icon: <IconRulerMeasure size={18} />,
    },
    {
      label: 'Car garage',
      value: property.garageSpaces,
      icon: <IconCarGarage size={18} />,
    },
  ].filter((fact) => fact.value != null);

  return (
    <Col className="w-full items-center">
      <Col className="w-full max-w-[1440px] px-4 pt-4 md:px-8 md:pt-6 lg:px-[60px]">
        <PdpPhotoViewer
          key={property.addressToken}
          ref={viewerRef}
          property={property}
          onShare={onShare}
          heroOverlay={heroOverlay}
        />
        <Box className="grid min-w-0 grid-cols-1 gap-x-20 pt-8 lg:grid-cols-[minmax(0,1fr)_29rem] lg:pt-10">
          <Col className="min-w-0">
            <RentalScamBanner />
            <Row
              role="group"
              aria-label="Listing status and activity"
              className="flex-wrap items-center gap-x-4 gap-y-3 pb-7 empty:hidden"
            >
              <PropertyStatus property={property} appearance="refresh" />
              {isActionable && isRecentlyListed(property.daysOnMarket) && (
                <PdpStatusBadge label="Newly listed" tone="new" />
              )}
              {priceDrop && <PdpStatusBadge label="Price drop" tone="neutral" />}
              <PdpEngagement engagement={engagement} />
            </Row>
            <Text as="h1" className="text-balance text-subheader-lg md:text-header-md">
              {address}
            </Text>
            <Text
              as="p"
              className="pt-3 text-body-lg text-od-content-secondary md:text-subheader-sm"
            >
              {locality}
            </Text>
            <Box
              role="group"
              aria-label="Property summary"
              className="grid w-full grid-cols-2 gap-2 pt-8 pb-[60px] lg:max-w-[504px] lg:grid-cols-[repeat(auto-fit,120px)]"
            >
              {facts.map(({ label, value, icon }) => (
                <Col
                  key={label}
                  className="h-[75px] w-full min-w-0 justify-between rounded-lg border border-od-border-primary p-[11px] lg:w-[120px]"
                >
                  <Box aria-hidden="true" className="text-od-content-inactive-primary">
                    {icon}
                  </Box>
                  <Text className="whitespace-nowrap text-body-md text-od-content-primary">
                    {value} {label.toLowerCase()}
                  </Text>
                </Col>
              ))}
            </Box>
          </Col>
          <Col className="min-w-0 pb-[60px] lg:row-span-2 lg:col-start-2 lg:row-start-1 lg:pb-[100px]">
            <Col
              ref={sidebarRef}
              className="gap-6 lg:sticky lg:top-[min(6rem,calc(100dvh-var(--pdp-sidebar-height,0px)-1rem))]"
            >
              <Box ref={priceCardRef}>
                <PdpPriceCard property={property} />
              </Box>
              {isActionable && property.shouldShowGuarantee && <PdpGuaranteeCard />}
            </Col>
          </Col>
          {/* Sections sit 60px apart; the 100px bottom padding is the gap above Similar homes. */}
          <Col className="min-w-0 gap-[60px] pb-7 lg:col-start-1 lg:pb-[100px]">
            {/* Floor plans belong to About this home, so they sit 40px below it instead of 60. */}
            <Col className="min-w-0 gap-10">
              <HomeDetails property={property} layout="overview" />
              <PdpFloorPlans
                key={property.addressToken}
                property={property}
                onOpenPhoto={openFloorPlan}
              />
            </Col>
            <PdpSchools schools={property.schools} />
            {property.latitude != null && property.longitude != null && (
              <Col as="section" aria-label="Location" className="gap-6">
                <Text as="h2" className="text-label-lg">
                  Location
                </Text>
                <Box className="h-64 overflow-hidden rounded-xl">
                  <PdpLocationMap latitude={property.latitude} longitude={property.longitude} />
                </Box>
              </Col>
            )}
            <PdpPriceHistory listingHistory={property.listingHistory} />
          </Col>
        </Box>
      </Col>
      {similarProperties.length > 0 && (
        <Col as="section" aria-label="Similar homes" className="w-full items-center bg-[#f9f8f5]">
          <Box className="w-full max-w-[1440px] px-5 py-8 md:px-8 lg:px-[60px] lg:py-[60px]">
            <SimilarListings
              key={property.addressToken}
              wide
              title={
                property.city
                  ? `Similar homes in ${property.city}${property.state ? `, ${property.state}` : ''}`
                  : 'Similar homes'
              }
              listings={similarProperties}
            />
          </Box>
        </Col>
      )}
      {isActionable && <Box className="h-28 lg:hidden" />}
      <Box className="pointer-events-none fixed inset-x-0 bottom-0 z-[4] lg:hidden">
        <FooterCTA property={property} isVisible={!inView} />
      </Box>
    </Col>
  );
};
